import { usePopup } from '@/hooks/usePopup';
import type { ContatoEmergencia } from '@/services/datasources/contatos';
import { agruparContatos } from '@/domain/contatos/agrupar';
import { EtiquetasFuncao } from '@/ui/EtiquetasFuncao';
import { PhoneIcon } from './PhoneIcon';

interface EmergencyContactsModalProps {
  isOpen: boolean;
  contatos: ContatoEmergencia[];
  isLoading: boolean;
  /** Falha ao carregar a cadeia (rede/RLS) — estado distinto de "vazio". */
  isError: boolean;
  onClose: () => void;
  /**
   * Navega para o diretório completo (Outros → Contatos). Injetado pelo
   * container (modal é puro, não conhece o router). Ausente → botão desabilitado.
   */
  onIrParaContatos?: () => void;
}

/** Cor da cadeia técnica interna (amarelo JF) e dos órgãos externos (vermelho). */
const COR_INTERNA = '#FECB00';
const COR_EXTERNA = '#ef4444';

/** Botão/etiqueta de telefone — LARGURA FIXA (todos iguais, no maior número). */
const BTN_TEL =
  'inline-flex items-center justify-center gap-1.5 w-[154px] px-3 py-1.5 rounded-lg text-xs font-extrabold whitespace-nowrap';

function LinhaContato({ contato, cor }: { contato: ContatoEmergencia; cor: string }) {
  const temTelefone = contato.phone !== '';
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white/5">
      <div className="min-w-0">
        <p className="text-[13px] font-bold text-white truncate">{contato.name}</p>
        {/* GUPM-202: uma pessoa, um cartão — cada função é uma etiqueta. */}
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <EtiquetasFuncao contato={contato} />
          {contato.disponibilidade ? (
            <span className="text-[10.5px] text-jf-dim">{contato.disponibilidade}</span>
          ) : null}
        </div>
      </div>
      {temTelefone ? (
        <a href={`tel:${contato.phone}`} className={BTN_TEL} style={{ background: cor, color: '#0f1729' }}>
          <PhoneIcon size={13} />
          {contato.display}
        </a>
      ) : (
        // pendente: sem link de ligação (lacuna da cadeia), mesmo tamanho
        <span className={`${BTN_TEL} italic font-semibold text-jf-dim border border-jf-border`}>
          {contato.display}
        </span>
      )}
    </div>
  );
}

function GrupoContatos({ titulo, cor, contatos }: { titulo: string; cor: string; contatos: ContatoEmergencia[] }) {
  if (contatos.length === 0) return null;
  return (
    <div className="mb-3">
      <p className="text-[11px] font-extrabold uppercase tracking-wide mb-1.5" style={{ color: cor }}>
        {titulo}
      </p>
      <div className="flex flex-col gap-1.5">
        {contatos.map((c) => (
          <LinhaContato key={c.id} contato={c} cor={cor} />
        ))}
      </div>
    </div>
  );
}

/**
 * Modal de contatos de emergência (cadeia PAEBM) — GUPM-158.
 * Alinhado ao `showEmergencyContacts` do protótipo v5.10: dois grupos (cadeia
 * técnica interna / órgãos externos), telefones clicáveis (`tel:`), pendentes
 * sem link. Somente LEITURA (contato é 🟠 sensível) — nenhuma ação de escrita.
 * Puro: recebe dados por props; o agrupamento vem do domínio (`agruparContatos`).
 *
 * Estados são distintos: carregando ≠ erro ≠ vazio. Numa cadeia de emergência,
 * um erro de fetch NÃO pode se disfarçar de "nenhum contato" (achado review #93).
 *
 * C-91: Esc fecha (mesmo caminho do "Fechar"), o foco entra no popup e volta ao
 * botão 🚨 ao fechar — `usePopup`.
 */
export function EmergencyContactsModal({ isOpen, contatos, isLoading, isError, onClose, onIrParaContatos }: EmergencyContactsModalProps) {
  const dialogoRef = usePopup<HTMLDivElement>({ aberto: isOpen, aoFechar: onClose });

  if (!isOpen) return null;

  const { interna, externa } = agruparContatos(contatos);
  const vazio = !isLoading && !isError && contatos.length === 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/60 p-4 pt-20"
      onClick={onClose}
    >
      <div
        ref={dialogoRef}
        role="dialog"
        aria-modal="true"
        aria-label="Contatos de emergência"
        tabIndex={-1}
        className="focus:outline-none w-full max-w-lg rounded-xl bg-jf-surface border border-jf-border shadow-xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 pt-5 pb-3">
          <h2 className="text-base font-black text-red-500 flex items-center gap-2">
            <PhoneIcon size={17} />
            Contatos de Emergência
          </h2>
          <p className="text-xs text-jf-muted mt-1.5 leading-relaxed">
            Toque no número para ligar. Em emergência, siga o PAEBM e o protocolo TARP.
          </p>
        </header>

        <div className="overflow-y-auto px-5">
          {isLoading ? (
            <p className="text-jf-muted text-sm py-2">Carregando contatos…</p>
          ) : isError ? (
            <p role="alert" className="text-red-300 text-sm py-2">
              Não foi possível carregar os contatos. Verifique a conexão e tente novamente.
            </p>
          ) : vazio ? (
            <p className="text-jf-muted text-sm py-2">
              Nenhum contato cadastrado. O cadastro da cadeia PAEBM é feito pelo administrador.
            </p>
          ) : (
            <>
              <GrupoContatos titulo="Cadeia técnica (interna)" cor={COR_INTERNA} contatos={interna} />
              <GrupoContatos titulo="Órgãos e emergência (externos)" cor={COR_EXTERNA} contatos={externa} />
            </>
          )}

          <p className="text-[10.5px] text-jf-dim border-t border-jf-border pt-2 mt-1 leading-relaxed">
            Lista completa de suporte por empresa no módulo 📚 Outros → Contatos.
          </p>
        </div>

        <footer className="flex gap-2.5 px-5 pt-3 pb-5 border-t border-jf-border">
          {/* Atalho pro diretório completo — Outros → Contatos já existe (GUPM-114/115).
              O container injeta a navegação (modal puro não conhece o router). */}
          <button
            type="button"
            onClick={onIrParaContatos}
            disabled={!onIrParaContatos}
            className="flex-1 py-[7px] px-[14px] rounded-lg text-xs font-bold bg-transparent text-jf-muted border border-jf-border transition-colors hover:border-jf-yellow/40 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            📚 Ir para Contatos
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-[7px] px-[14px] rounded-lg text-xs font-bold text-white bg-red-500 hover:bg-red-600 transition-colors"
          >
            Fechar
          </button>
        </footer>
      </div>
    </div>
  );
}
